import { zodResolver } from '@hookform/resolvers/zod';
import { resetPasswordSchema } from '@pgs/shared';
import { useMutation } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Chrome } from '@/components/layout/Chrome';
import { api, type ApiError } from '@/lib/api';

const formSchema = resetPasswordSchema
  .extend({
    confirm: z.string().min(8, 'Confirm your password'),
  })
  .refine((v) => v.password === v.confirm, {
    path: ['confirm'],
    message: 'Passwords do not match',
  });

type FormInput = z.infer<typeof formSchema>;

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormInput>({
    resolver: zodResolver(formSchema),
    defaultValues: { token },
  });

  const mutation = useMutation({
    mutationFn: async (input: FormInput) => {
      await api.post('/auth/reset-password', { token: input.token, password: input.password });
    },
  });

  const serverMessage =
    mutation.error instanceof AxiosError
      ? (mutation.error.response?.data as ApiError | undefined)?.error?.message
      : undefined;

  if (!token) {
    return (
      <Chrome>
        <div className="mx-auto max-w-md px-6 py-16">
          <div className="rounded bg-white p-8 shadow-cardLg">
            <h1 className="mb-2 text-2xl font-black text-navy">Invalid link</h1>
            <p className="mb-6 text-sm text-ink-500">
              This reset link is missing its token. Request a new one from the forgot-password
              page.
            </p>
            <button
              type="button"
              onClick={() => navigate('/forgot-password')}
              className="btn-navy w-full"
            >
              Request a new link
            </button>
          </div>
        </div>
      </Chrome>
    );
  }

  return (
    <Chrome>
      <div className="mx-auto max-w-md px-6 py-16">
        <div className="rounded bg-white p-8 shadow-cardLg">
          <h1 className="mb-2 text-2xl font-black text-navy">Choose a new password</h1>
          <p className="mb-6 text-sm text-ink-500">
            Enter a new password for your account. You'll be signed out of any other devices.
          </p>

          {mutation.isSuccess ? (
            <div className="rounded-sm border-l-[3px] border-teal bg-teal-tint p-4 text-sm text-ink-700">
              <p className="font-bold text-navy">Password updated.</p>
              <p className="mt-1">You can now sign in with your new password.</p>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="btn-navy mt-4 w-full"
              >
                Go to login
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit((v) => mutation.mutate(v))} className="space-y-4">
              <input type="hidden" {...register('token')} />
              <div>
                <label className="lbl">New password</label>
                <input
                  {...register('password')}
                  type="password"
                  className={`inp ${errors.password ? 'inp-error' : ''}`}
                  placeholder="At least 8 characters"
                  autoComplete="new-password"
                />
                {errors.password && <p className="err-msg">{errors.password.message}</p>}
              </div>
              <div>
                <label className="lbl">Confirm password</label>
                <input
                  {...register('confirm')}
                  type="password"
                  className={`inp ${errors.confirm ? 'inp-error' : ''}`}
                  placeholder="Repeat your password"
                  autoComplete="new-password"
                />
                {errors.confirm && <p className="err-msg">{errors.confirm.message}</p>}
              </div>

              {serverMessage && <p className="text-sm text-red-600">{serverMessage}</p>}

              <button type="submit" disabled={mutation.isPending} className="btn-navy w-full">
                {mutation.isPending ? 'Updating…' : 'Update password'}
              </button>
              <button
                type="button"
                onClick={() => navigate('/login')}
                className="mt-2 block w-full text-center text-[11px] uppercase tracking-wider text-ink-500 hover:text-navy"
              >
                Back to login
              </button>
            </form>
          )}
        </div>
      </div>
    </Chrome>
  );
};
